/**
 * Patient CRUD — all operations scoped to organizations/{orgId}/patients.
 *
 * Deletion goes through the `deletePatient` Cloud Function (never a client
 * `deleteDoc`) so every patient removal is audited (HIPAA #185) —
 * firestore.rules denies client deletes on patients.
 */

import { addDoc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'
import type { Patient } from '../types'
import { patientsCollection, patientRef } from './firestore'
import { logStaffEdit } from './audit'

/** Create a new patient. Returns the generated Firestore ID. */
export async function addPatient(orgId: string, patient: Omit<Patient, 'id'>): Promise<string> {
  const ref = await addDoc(patientsCollection(orgId), { ...patient, id: '' } as Patient)
  logStaffEdit('patients', ref.id, 'create')
  return ref.id
}

/** Fetch a single patient by ID. Returns null if not found. */
export async function getPatient(orgId: string, patientId: string): Promise<Patient | null> {
  const snap = await getDoc(patientRef(orgId, patientId))
  return snap.exists() ? snap.data() : null
}

/** List patients for an organization, optionally filtered by clinic. */
export async function listPatients(orgId: string, clinicId?: string): Promise<Patient[]> {
  const ref = patientsCollection(orgId)
  const q = clinicId
    ? query(ref, where('clinicId', '==', clinicId))
    : ref
  const snap = await getDocs(q)
  return snap.docs.map(d => d.data())
}

/** Partial update of a patient document. */
export async function updatePatient(
  orgId: string,
  patientId: string,
  fields: Partial<Omit<Patient, 'id'>>,
): Promise<void> {
  await updateDoc(patientRef(orgId, patientId), fields)
  logStaffEdit('patients', patientId, 'update')
}

/** Delete a patient by ID — via the audited deletePatient callable (#185). */
export async function deletePatient(_orgId: string, patientId: string): Promise<void> {
  await httpsCallable(functions, 'deletePatient')({ patientId })
}
